/*
 * Geräte-Freigaben, /settings/device-registrations.
 *
 * Geräte, die sich selbst angemeldet haben (sinnux auf TP8 Pro / TP10),
 * warten hier auf Freigabe. Freigeben heisst: Anlage wählen und den Code
 * eintippen, den das Gerät anzeigt. Der Code ist der Beweis, dass man vor
 * genau diesem Gerät steht; die Seriennummer allein ist kein Geheimnis.
 *
 * Nur mit `devices.registration.admin` (Gruppe Admins): ein wartendes Gerät
 * gehört noch keinem Datenbereich, die Liste zeigt Seriennummern über alle
 * Mandanten. Entwurf: docs/entwurf-geraete-selbstanmeldung.md.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { AppShell } from '../components/AppShell';
import { Table, type TableColumn } from '../components/ui/Table';
import { StatusBadge } from '../components/ui/StatusBadge';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { showToast } from '../components/Toaster';
import { showConfirm } from '../components/Confirmer';
import { sessionSignal } from '../auth/session';
import type { ListPlantsResponse, PlantSummary } from '../api/types';
import {
  codeMismatch,
  codesMatch,
  filterPlants,
  formatRelative,
  statusLabel,
  statusTone,
  type DeviceRegistrationDto,
  type ListDeviceRegistrationsResponse,
} from '../settings/deviceRegistrations';
import './DeviceRegistrationsPage.css';

const PERMISSION = 'devices.registration.admin';
const REFRESH_MS = 15_000;

function errorText(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string; detail?: string; title?: string } | undefined;
    return `HTTP ${err.status}: ${body?.error ?? body?.detail ?? body?.title ?? err.message}`;
  }
  return err instanceof Error ? err.message : String(err);
}

export function DeviceRegistrationsPage() {
  const canAdmin = (sessionSignal.value.user?.permissions ?? []).includes(PERMISSION);
  const [tick, setTick] = useState(0);
  const [approving, setApproving] = useState<DeviceRegistrationDto | null>(null);

  // Neue Anmeldungen erscheinen ohne Neuladen: ein Gerät meldet sich, während
  // jemand mit dieser Seite davorsteht.
  useEffect(() => {
    if (!canAdmin) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), REFRESH_MS);
    return () => clearInterval(t);
  }, [canAdmin]);

  const list = useAsync(
    (signal) =>
      canAdmin
        ? api.get<ListDeviceRegistrationsResponse>('/api/device-registrations', signal)
        : Promise.resolve<ListDeviceRegistrationsResponse>({ registrations: [] }),
    [canAdmin, tick],
  );
  const plants = useAsync(
    (signal) =>
      canAdmin
        ? api.get<ListPlantsResponse>('/api/plants', signal)
        : Promise.resolve<ListPlantsResponse>({ plants: [] }),
    [canAdmin],
  );
  const plantName = useMemo(() => {
    const map = new Map<string, string>();
    if (plants.status === 'ok') for (const p of plants.data.plants) map.set(p.id, p.displayName);
    return map;
  }, [plants]);

  if (!canAdmin) {
    return (
      <AppShell title="Geräte-Freigaben">
        <div class="hv-devreg__empty">
          Für Geräte-Freigaben fehlt das Recht <code>{PERMISSION}</code>. Wartende Geräte gehören
          noch keiner Mandantengruppe, deshalb sehen sie nur Systemadmins.
        </div>
      </AppShell>
    );
  }

  const rows = list.status === 'ok' ? list.data.registrations : [];
  const pending = rows.filter((r) => r.status === 'pending');
  const decided = rows.filter((r) => r.status !== 'pending');

  const reject = async (r: DeviceRegistrationDto) => {
    const ok = await showConfirm({
      title: 'Anmeldung ablehnen?',
      message: `Gerät ${r.chipSerial} (Code ${r.code}) wird abgelehnt. Es kann sich erst mit einem neuen Schlüssel wieder anmelden.`,
      confirmLabel: 'Ablehnen',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.post(`/api/device-registrations/${r.id}/reject`, {});
      showToast({ tone: 'success', title: 'Anmeldung abgelehnt', message: r.chipSerial });
      setTick((n) => n + 1);
    } catch (err) {
      showToast({ tone: 'error', title: 'Ablehnen fehlgeschlagen', message: errorText(err) });
    }
  };

  const columns: TableColumn<DeviceRegistrationDto>[] = [
    { key: 'code', header: 'Code', render: (r) => <code class="hv-devreg__code">{r.code}</code>, width: '120px' },
    { key: 'chip', header: 'Chip-Nummer', render: (r) => <code class="hv-devreg__serial">{r.chipSerial}</code> },
    { key: 'nameplate', header: 'Typenschild', render: (r) => <span class="hv-devreg__serial">{r.nameplateSerial ?? ''}</span> },
    { key: 'host', header: 'Gerät', render: (r) => [r.hostname, r.chassis].filter(Boolean).join(' · ') },
    {
      key: 'seen',
      header: 'Zuletzt gemeldet',
      render: (r) => (
        <span title={new Date(r.lastSeenAt).toLocaleString('de-AT')}>
          {formatRelative(r.lastSeenAt)}{r.lastSourceIp ? ` · ${r.lastSourceIp}` : ''}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '220px',
      render: (r) => (
        <div class="hv-devreg__actions">
          <button type="button" class="hv-btn hv-btn--sm hv-btn--primary" onClick={() => setApproving(r)}>
            Freigeben
          </button>
          <button type="button" class="hv-btn hv-btn--sm hv-btn--ghost" onClick={() => void reject(r)}>
            Ablehnen
          </button>
        </div>
      ),
    },
  ];

  const decidedColumns: TableColumn<DeviceRegistrationDto>[] = [
    { key: 'code', header: 'Code', render: (r) => <code class="hv-devreg__code">{r.code}</code>, width: '120px' },
    { key: 'chip', header: 'Chip-Nummer', render: (r) => <code class="hv-devreg__serial">{r.chipSerial}</code> },
    { key: 'nameplate', header: 'Typenschild', render: (r) => <span class="hv-devreg__serial">{r.nameplateSerial ?? ''}</span> },
    {
      key: 'plant',
      header: 'Anlage',
      render: (r) => (r.plantId ? plantName.get(r.plantId) ?? r.plantId : ''),
    },
    {
      key: 'status',
      header: 'Zustand',
      render: (r) => <StatusBadge tone={statusTone(r)}>{statusLabel(r)}</StatusBadge>,
    },
    {
      key: 'decided',
      header: 'Entschieden',
      render: (r) => (r.decidedAt ? formatRelative(r.decidedAt) : ''),
    },
  ];

  return (
    <AppShell title="Geräte-Freigaben">
      <div class="hv-devreg">
        <p class="hv-devreg__intro">
          Geräte mit Selbstanmeldung (sinnux) warten hier, bis sie einer Anlage zugeordnet sind.
          Zum Freigeben den Code eintippen, den das Gerät anzeigt.
        </p>

        <h2 class="hv-devreg__heading">Warten auf Freigabe</h2>
        {list.status === 'loading' && rows.length === 0 && <div class="hv-devreg__empty">Lade Anmeldungen…</div>}
        {list.status === 'error' && <div class="hv-devreg__empty">Anmeldungen konnten nicht geladen werden.</div>}
        {list.status !== 'error' && (list.status === 'ok' || rows.length > 0) && (
          <Table columns={columns} rows={pending} rowKey={(r) => r.id} emptyLabel="Kein Gerät wartet." />
        )}

        {decided.length > 0 && (
          <>
            <h2 class="hv-devreg__heading">Entschieden</h2>
            <Table columns={decidedColumns} rows={decided} rowKey={(r) => r.id} />
          </>
        )}
      </div>

      {approving && (
        <ApproveDialog
          registration={approving}
          plants={plants.status === 'ok' ? plants.data.plants : []}
          plantsLoading={plants.status === 'loading'}
          onClose={() => setApproving(null)}
          onApproved={() => {
            setApproving(null);
            setTick((n) => n + 1);
          }}
        />
      )}
    </AppShell>
  );
}

interface ApproveDialogProps {
  registration: DeviceRegistrationDto;
  plants: PlantSummary[];
  plantsLoading: boolean;
  onClose: () => void;
  onApproved: () => void;
}

function ApproveDialog({ registration, plants, plantsLoading, onClose, onApproved }: ApproveDialogProps) {
  const [query, setQuery] = useState('');
  const [plantId, setPlantId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hits = useMemo(() => filterPlants(plants, query), [plants, query]);
  const chosen = plants.find((p) => p.id === plantId) ?? null;
  const ready = chosen !== null && codesMatch(code, registration.code) && !busy;

  const submit = async () => {
    if (!chosen) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/device-registrations/${registration.id}/approve`, { plantId: chosen.id, code });
      showToast({
        tone: 'success',
        title: 'Gerät freigegeben',
        message: `${registration.chipSerial} → ${chosen.displayName}. Das Gerät richtet sich bei der nächsten Abfrage ein.`,
      });
      onApproved();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={onClose}>
      <div class="hv-modal hv-modal--wide" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Gerät freigeben</h2>
          <button type="button" class="hv-modal__close" onClick={onClose} aria-label="Schließen">×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-devreg__device">
            <div><span>Chip-Nummer</span><code>{registration.chipSerial}</code></div>
            {registration.nameplateSerial && (
              <div><span>Typenschild</span><code>{registration.nameplateSerial}</code></div>
            )}
            {registration.hostname && <div><span>Gerät</span>{registration.hostname}</div>}
          </div>

          <div class="hv-modal__field">
            <label for="devreg-plant-search">Anlage</label>
            <input
              id="devreg-plant-search"
              type="search"
              placeholder="Name oder Adresse"
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="hv-devreg__plants" role="listbox" aria-label="Anlagen">
            {plantsLoading && <div class="hv-devreg__empty">Lade Anlagen…</div>}
            {!plantsLoading && hits.length === 0 && <div class="hv-devreg__empty">Keine passende Anlage.</div>}
            {hits.map((p) => (
              <button
                key={p.id}
                type="button"
                role="option"
                aria-selected={p.id === plantId}
                class={'hv-devreg__plant' + (p.id === plantId ? ' hv-devreg__plant--active' : '')}
                onClick={() => setPlantId(p.id)}
              >
                <span class="hv-devreg__plant-name">{p.displayName}</span>
                {p.address && <span class="hv-devreg__plant-address">{p.address}</span>}
              </button>
            ))}
          </div>

          <div class="hv-modal__field">
            <label for="devreg-code">Code vom Gerät</label>
            <input
              id="devreg-code"
              type="text"
              class="hv-devreg__code-input"
              placeholder="XXXX-XXXX"
              autocomplete="off"
              spellcheck={false}
              maxLength={12}
              value={code}
              onInput={(e) => setCode((e.target as HTMLInputElement).value)}
            />
          </div>
          <div class="hv-modal__hint">
            Der Code steht auf der Infoseite des Geräts. Er muss zu genau dieser Anmeldung passen;
            eine zweite Anmeldung mit derselben Chip-Nummer hätte einen anderen.
          </div>
          {codeMismatch(code, registration.code) && (
            <div class="hv-modal__error">Der Code passt nicht zu dieser Anmeldung.</div>
          )}
          {error && <div class="hv-modal__error">{error}</div>}
        </div>
        <div class="hv-modal__footer">
          <span class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn hv-btn--secondary" onClick={onClose}>Abbrechen</button>
          <button type="button" class="hv-btn hv-btn--primary" disabled={!ready} onClick={() => void submit()}>
            {busy ? 'Gebe frei…' : 'Freigeben'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
